import PropTypes from 'prop-types';
import { Chart } from 'react-chartjs-2';
import { numberFormat } from '@/utils/unitUtils';

const POSITIVE = '#10b981';
const NEGATIVE = '#ef4444';
const CUMULATIVE = '#1d4ed8';
const ANNOTATION_COLOR = '#475569';

// Find a "nice" tick step (1/2/5 × 10^k) that splits a range into ~5 ticks.
const niceStep = range => {
  if (range <= 0) return 1;
  const rough = range / 5;
  const pow10 = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow10;
  let nice;
  if (norm < 1.5) nice = 1;
  else if (norm < 3) nice = 2;
  else if (norm < 7) nice = 5;
  else nice = 10;
  return nice * pow10;
};

const floorTo = (value, step) => Math.floor(value / step) * step;
const ceilTo = (value, step) => Math.ceil(value / step) * step;

// Compute aligned min/max so the two linear y-axes share the same zero pixel,
// AND so each axis's bounds land on multiples of its tick step. We expand each
// axis to the nearest nice step (so 34.49 → 40, -113.37 → -120), then re-align
// the zero by extending whichever axis's negative side is too short — always
// by an integer multiple of that axis's step so the bounds stay tick-aligned.
const alignZeros = (annual, cumulative) => {
  const annualMin = Math.min(0, ...annual);
  const annualMax = Math.max(0, ...annual);
  const cumMin = Math.min(0, ...cumulative);
  const cumMax = Math.max(0, ...cumulative);

  const stepA = niceStep(annualMax - annualMin);
  const stepC = niceStep(cumMax - cumMin);

  let minA = floorTo(annualMin, stepA);
  let maxA = ceilTo(annualMax, stepA);
  let minC = floorTo(cumMin, stepC);
  let maxC = ceilTo(cumMax, stepC);

  // Iteratively extend whichever axis has the smaller negative proportion by
  // one step down until both axes share (approximately) the same negProp.
  // Capped at 50 iterations as a safety belt; in practice this converges in
  // a handful of steps because each side is already near the desired ratio.
  for (let i = 0; i < 50; i++) {
    const rangeA = maxA - minA || 1;
    const rangeC = maxC - minC || 1;
    const negA = -minA / rangeA;
    const negC = -minC / rangeC;
    if (Math.abs(negA - negC) < 0.005) break;
    if (negA < negC) {
      minA -= stepA;
    } else {
      minC -= stepC;
    }
  }

  return {
    annual: { min: minA, max: maxA, step: stepA },
    cumulative: { min: minC, max: maxC, step: stepC },
  };
};

// Custom Chart.js plugin that draws a dashed vertical line at the payback year
// with a small label pinned to the top of the chart area. Falls back silently
// when payback isn't achieved (paybackYears == null).
const buildPaybackAnnotationPlugin = paybackYears => ({
  id: 'paybackAnnotation',
  afterDatasetsDraw(chart) {
    if (paybackYears == null) return;
    const xScale = chart.scales.x;
    if (!xScale) return;

    const xPixel = xScale.getPixelForValue(paybackYears);
    if (xPixel == null || Number.isNaN(xPixel)) return;

    const { ctx, chartArea } = chart;
    ctx.save();

    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = ANNOTATION_COLOR;
    ctx.lineWidth = 1;
    ctx.moveTo(xPixel, chartArea.top);
    ctx.lineTo(xPixel, chartArea.bottom);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.fillStyle = ANNOTATION_COLOR;
    ctx.font = '600 11px ui-sans-serif, system-ui, sans-serif';
    const label = `Payback · ${numberFormat(paybackYears, 1)} yrs`;
    const labelWidth = ctx.measureText(label).width;
    const labelX = xPixel + labelWidth + 8 > chartArea.right ? xPixel - labelWidth - 4 : xPixel + 4;
    ctx.fillText(label, labelX, chartArea.top + 12);

    ctx.restore();
  },
});

const CumulativeCashFlowChart = ({ labels, cumulative, annual, yAxisLabel, paybackYears }) => {
  const data = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Annual net cash flow',
        data: annual,
        backgroundColor: annual.map(value => (value < 0 ? '#fecaca' : '#bbf7d0')),
        borderColor: annual.map(value => (value < 0 ? NEGATIVE : POSITIVE)),
        borderWidth: 1,
        order: 2,
        yAxisID: 'yAnnual',
      },
      {
        type: 'line',
        label: 'Cumulative cash flow',
        data: cumulative,
        borderColor: CUMULATIVE,
        backgroundColor: CUMULATIVE,
        pointBackgroundColor: CUMULATIVE,
        pointRadius: 3,
        tension: 0.2,
        fill: false,
        order: 1,
        yAxisID: 'yCumulative',
      },
    ],
  };

  const unitSuffix = yAxisLabel ? ` (${yAxisLabel})` : '';
  const aligned = alignZeros(annual, cumulative);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${numberFormat(ctx.parsed.y, 2)}`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: 'Year' },
        grid: { display: false },
      },
      yAnnual: {
        type: 'linear',
        position: 'left',
        min: aligned.annual.min,
        max: aligned.annual.max,
        title: { display: true, text: `Annual${unitSuffix}` },
        ticks: {
          stepSize: aligned.annual.step,
          callback: value => numberFormat(value),
        },
        grid: {
          color: ctx => (ctx.tick.value === 0 ? '#0f172a' : '#e5e7eb'),
          lineWidth: ctx => (ctx.tick.value === 0 ? 1.5 : 1),
        },
      },
      yCumulative: {
        type: 'linear',
        position: 'right',
        min: aligned.cumulative.min,
        max: aligned.cumulative.max,
        title: { display: true, text: `Cumulative${unitSuffix}` },
        ticks: {
          stepSize: aligned.cumulative.step,
          callback: value => numberFormat(value),
        },
        grid: { drawOnChartArea: false },
      },
    },
  };

  if (annual.length === 0) {
    return <div className="text-gray-400 text-sm flex items-center justify-center h-full">No cash flow data</div>;
  }

  return (
    <div className="h-full w-full">
      <Chart type="bar" data={data} options={options} plugins={[buildPaybackAnnotationPlugin(paybackYears)]} />
    </div>
  );
};

CumulativeCashFlowChart.propTypes = {
  labels: PropTypes.array.isRequired,
  cumulative: PropTypes.array.isRequired,
  annual: PropTypes.array.isRequired,
  yAxisLabel: PropTypes.string,
  paybackYears: PropTypes.number,
};

export default CumulativeCashFlowChart;
